import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ResumptionRequestFormRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath, RequestTypeCodes } from '@Enum/index';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import { AttendanceTime } from '@Types/api/form/resumptionRequest';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { ResumptionLeavesResponse } from '@Types/api/form/resumptionRequest/ResumpationLeaves';

const REQ_TYPE_FILTER = {
  name: 'requestTypeID',
  value: RequestTypeCodes.RESUMPTION_REQUEST,
  operator: 'eq',
};

export const resumptionRequestApi = createApi({
  tagTypes: ['resumptionRequest'],
  reducerPath: ApiReducerPath.API_RESUMPTION_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getResumptionRequests: builder.mutation<
      RequestListSearchResponse<HrmRequestFormResponse>,
      RequestListSearchRequest
    >({
      query: ({
        searchValue,
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'requestId',
        sortOrder = 'desc',
      }) => ({
        url: `${ApiBaseName.ESS}/hrm-request/search`,
        method: 'post',
        body: {
          searchValue,
          searchCriteriaList: [
            {
              searchCriterias: [...filters, REQ_TYPE_FILTER],
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getResumptionLeaves: builder.mutation<
      ResumptionLeavesResponse,
      { empNumber: string; resumptionReqId?: number }
    >({
      query: ({ empNumber, resumptionReqId }) => ({
        url: `${ApiBaseName.ESS}/hrm-request/leaves_for_resumption`,
        method: 'get',
        params: { empNumber, currentResumptionRequestId: resumptionReqId },
      }),
    }),
    getAttendanceTime: builder.mutation<AttendanceTime, string>({
      query: (resumeDate) => ({
        url: `${ApiBaseName.ESS}/hrm-request/resumption_attributes`,
        method: 'get',
        params: { resumeDate },
      }),
    }),
    saveDraftResumptionRequest: builder.mutation<
      HrmRequestFormResponse,
      ResumptionRequestFormRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/hrm-request`,
        method: 'post',
        body: {
          ...body,
          requestTypeID: RequestTypeCodes.RESUMPTION_REQUEST,
        },
      }),
    }),
  }),
});

export const {
  useGetResumptionRequestsMutation,
  useGetResumptionLeavesMutation,
  useSaveDraftResumptionRequestMutation,
  useGetAttendanceTimeMutation,
} = resumptionRequestApi;
